import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { isHandledError } from "@/lib/api/errors";
import { Plus, Pencil, Trash2, SlidersHorizontal, Power } from "lucide-react";
import {
  useAvailablePipelineSteps,
  useCreateSimulation,
  useDeleteSimulation,
  useSetCurrentSimulation,
  useSimulationsList,
  useUpdateSimulation,
} from "@/hooks/api/useSimulations";
import type {
  AvailableStep,
  CreateSimulationBody,
  PipelineCommand,
  SimulationConfig,
} from "@/services/simulations.service";

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function dateToYmd(date: Date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function monthToApiRange(month: string, isEnd: boolean) {
  // month: YYYY-MM
  const [y, m] = month.split("-").map((x) => Number(x));
  const monthIdx = m - 1;
  if (
    !Number.isFinite(y) ||
    !Number.isFinite(monthIdx) ||
    monthIdx < 0 ||
    monthIdx > 11
  ) {
    return "1970-01-01 00:00:00";
  }

  if (!isEnd) {
    const first = new Date(y, monthIdx, 1);
    return `${dateToYmd(first)} 00:00:00`;
  }

  const last = new Date(y, monthIdx + 1, 0);
  return `${dateToYmd(last)} 00:00:00`;
}

function isoToMonth(iso: string) {
  // "2025-10-01 00:00:00" -> "2025-10"
  const m = iso.match(/^(\d{4})-(\d{2})-\d{2}/);
  if (!m) return "";
  return `${m[1]}-${m[2]}`;
}

function formatMonthLabel(month: string) {
  const m = month.match(/^(\d{4})-(\d{2})$/);
  if (!m) return month;
  const year = Number(m[1]);
  const monthIdx = Number(m[2]) - 1;
  const d = new Date(year, monthIdx, 1);
  return d
    .toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
    .toUpperCase();
}

function formatMonthOnly(month: string) {
  const m = month.match(/^(\d{4})-(\d{2})$/);
  if (!m) return month;
  const year = Number(m[1]);
  const monthIdx = Number(m[2]) - 1;
  const d = new Date(year, monthIdx, 1);
  return d.toLocaleDateString("pt-BR", { month: "long" }).toUpperCase();
}

const MONTH_OPTIONS = [
  { value: "01", label: "Janeiro" },
  { value: "02", label: "Fevereiro" },
  { value: "03", label: "Março" },
  { value: "04", label: "Abril" },
  { value: "05", label: "Maio" },
  { value: "06", label: "Junho" },
  { value: "07", label: "Julho" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Setembro" },
  { value: "10", label: "Outubro" },
  { value: "11", label: "Novembro" },
  { value: "12", label: "Dezembro" },
];

function isStringCommandArray(v: unknown): v is PipelineCommand[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

function buildPipelineConfig(steps: AvailableStep[], startOrder: number) {
  const sorted = [...steps].sort((a, b) => a.order - b.order);
  return sorted
    .filter((s) => s.enabled && s.order >= startOrder)
    .map((s) => s.command);
}

function getStartOrderFromPipelineConfig(
  steps: AvailableStep[],
  pipelineConfig: unknown,
) {
  if (!isStringCommandArray(pipelineConfig)) return null;

  const orderByCommand = new Map<string, number>();
  steps.forEach((s) => orderByCommand.set(s.command, s.order));

  const orders: number[] = pipelineConfig
    .map((cmd) => orderByCommand.get(cmd))
    .filter((v): v is number => typeof v === "number");

  if (orders.length === 0) return null;
  return Math.min(...orders);
}

export default function SimulationConfigs() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const {
    data: simListResp,
    isLoading: isListLoading,
    isError: isListError,
    error: listError,
    refetch: refetchList,
  } = useSimulationsList();
  const simulations: SimulationConfig[] = simListResp?.data ?? [];

  const {
    data: stepsResp,
    isLoading: isStepsLoading,
    isError: isStepsError,
  } = useAvailablePipelineSteps({ enabled: true });

  const steps = stepsResp?.steps ?? [];
  const stepsSortedEnabled = useMemo(() => {
    return [...steps]
      .filter((s) => s.enabled)
      .sort((a, b) => a.order - b.order);
  }, [steps]);

  const minEnabledOrder = stepsSortedEnabled[0]?.order ?? 1;

  const createMutation = useCreateSimulation();
  const updateMutation = useUpdateSimulation();
  const deleteMutation = useDeleteSimulation();
  const setCurrentMutation = useSetCurrentSimulation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [editingId, setEditingId] = useState<number | null>(null);

  const [formName, setFormName] = useState("");
  const [baseMonth, setBaseMonth] = useState("");
  const [forecastStartMonth, setForecastStartMonth] = useState("");
  const [forecastEndMonth, setForecastEndMonth] = useState("");
  const [startOrder, setStartOrder] = useState<number>(minEnabledOrder);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const baseMonthOnly = useMemo(() => {
    const m = baseMonth.match(/^\d{4}-(\d{2})$/);
    return m ? m[1] : "";
  }, [baseMonth]);

  useEffect(() => {
    if (!dialogOpen || mode !== "create") return;
    // Default: base = atual, forecast = +1 e +3 meses
    if (baseMonth) return;
    const now = new Date();
    const y = now.getFullYear();
    const m = pad2(now.getMonth() + 1);
    setBaseMonth(`${y}-${m}`);
    const start = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 3, 1);
    setForecastStartMonth(
      `${start.getFullYear()}-${pad2(start.getMonth() + 1)}`,
    );
    setForecastEndMonth(`${end.getFullYear()}-${pad2(end.getMonth() + 1)}`);
    setStartOrder(minEnabledOrder);
  }, [dialogOpen, mode, baseMonth, minEnabledOrder]);

  const openCreate = () => {
    setMode("create");
    setEditingId(null);
    setFormName("");
    setBaseMonth("");
    setForecastStartMonth("");
    setForecastEndMonth("");
    setStartOrder(minEnabledOrder);
    setDialogOpen(true);
  };

  const openEdit = (cfg: SimulationConfig) => {
    setMode("edit");
    setEditingId(cfg.id);
    setFormName(cfg.name);
    setBaseMonth(isoToMonth(cfg.base_date_start));
    setForecastStartMonth(isoToMonth(cfg.forecast_date_start));
    setForecastEndMonth(isoToMonth(cfg.forecast_date_end));
    setDialogOpen(true);
    // startOrder depends on available steps; set it in effect below
  };

  useEffect(() => {
    if (!dialogOpen || mode !== "edit" || editingId == null) return;
    if (steps.length === 0) return;

    const cfg = simulations.find((s) => s.id === editingId);
    if (!cfg) return;

    const computed = getStartOrderFromPipelineConfig(
      steps,
      cfg.pipeline_config,
    );
    if (computed != null) setStartOrder(computed);
  }, [dialogOpen, mode, editingId, steps, simulations]);

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const requestDelete = (id: number) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    if (deleteId == null) return;
    try {
      await deleteMutation.mutateAsync(deleteId);
      toast({ title: "Simulação deletada" });
      await refetchList();
    } catch (err: any) {
      if (!isHandledError(err)) {
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          "Erro ao deletar simulação.";
        toast({ title: "Erro", description: msg, variant: "destructive" });
      }
    } finally {
      setDeleteOpen(false);
      setDeleteId(null);
    }
  };

  const handleSetCurrent = async (id: number) => {
    try {
      await setCurrentMutation.mutateAsync(id);
      toast({ title: "Simulação ativada" });
      await refetchList();
    } catch (err: any) {
      if (!isHandledError(err)) {
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          "Erro ao ativar simulação.";
        toast({ title: "Erro", description: msg, variant: "destructive" });
      }
    }
  };

  const buildCreateBody = (): CreateSimulationBody => {
    const baseStart = monthToApiRange(baseMonth, false);
    const baseEnd = monthToApiRange(baseMonth, true);
    const forecastStart = monthToApiRange(forecastStartMonth, false);
    const forecastEnd = monthToApiRange(forecastEndMonth, true);

    const pipeline_config = buildPipelineConfig(stepsSortedEnabled, startOrder);

    return {
      name: formName.trim(),
      parameters: [],
      base_date_start: baseStart,
      base_date_end: baseEnd,
      forecast_date_start: forecastStart,
      forecast_date_end: forecastEnd,
      pipeline_config,
    };
  };

  const handleSave = async () => {
    if (createMutation.isPending || updateMutation.isPending) return;

    if (!formName.trim()) {
      toast({
        title: "Erro",
        description: "Informe o nome da simulação.",
        variant: "destructive",
      });
      return;
    }
    if (!baseMonth || !forecastStartMonth || !forecastEndMonth) {
      toast({
        title: "Erro",
        description: "Selecione os meses de base e da simulação.",
        variant: "destructive",
      });
      return;
    }
    if (stepsSortedEnabled.length === 0) {
      toast({
        title: "Erro",
        description: "Sem passos disponíveis para montar a pipeline.",
        variant: "destructive",
      });
      return;
    }

    try {
      const body = buildCreateBody();
      if (mode === "edit" && editingId != null) {
        await updateMutation.mutateAsync({ id: editingId, data: body });
      } else {
        await createMutation.mutateAsync(body as any);
      }
      toast({ title: "Simulação salva" });
      closeDialog();
      await refetchList();
    } catch (err: any) {
      if (!isHandledError(err)) {
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          "Erro ao salvar simulação.";
        toast({ title: "Erro", description: msg, variant: "destructive" });
      }
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      <Navigation />

      <main className="flex-1 flex flex-col">
        <div className="border-b border-[#BAC9CA] bg-white">
          <div className="flex items-center justify-between px-5 py-5">
            <div className="flex items-center gap-3">
              <SlidersHorizontal className="w-6 h-6 text-[#00646B]" />
              <h1 className="text-[28px] font-medium text-[#151C1D]">
                Simulações
              </h1>
            </div>
            <Button
              onClick={openCreate}
              className="h-12 px-4 rounded-xl bg-[#00646B] hover:bg-[#00646B]/90 text-white"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nova simulação
            </Button>
          </div>
        </div>

        <div className="flex-1 px-5 py-9">
          <div className="rounded-[28px] overflow-hidden border border-[#BAC9CA]">
            <div className="bg-[#EDF4F5] px-6 py-4 border-b border-[#BAC9CA]">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-[#151C1D]">
                  Configurações
                </h2>
                <Badge className="h-6 px-2 rounded bg-[#D6E5E6] text-[#135045] text-xs font-semibold">
                  {simulations.length} itens
                </Badge>
              </div>
            </div>

            <div className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#EDF4F5] border-b border-[#BAC9CA] hover:bg-[#EDF4F5]">
                    <TableHead>Nome</TableHead>
                    <TableHead>Base (mês)</TableHead>
                    <TableHead>Forecast (mês/ano)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[380px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isListLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="h-32 text-center text-[#3B494A]"
                      >
                        Carregando...
                      </TableCell>
                    </TableRow>
                  ) : isListError ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="h-32 text-center text-[#93000A]"
                      >
                        {listError?.message ?? "Erro ao carregar simulações."}
                      </TableCell>
                    </TableRow>
                  ) : simulations.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="h-32 text-center text-[#3B494A]"
                      >
                        Nenhuma simulação encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    simulations.map((cfg) => (
                      <TableRow key={cfg.id} className="hover:bg-[#F8FBFB]">
                        <TableCell className="text-[#151C1D] font-medium">
                          <div className="flex flex-col">
                            <span>{cfg.name}</span>
                            <span className="text-xs text-[#3B494A]">
                              Criado por: {cfg.created_by || "—"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-[#3B494A]">
                          {cfg.base_date_start
                            ? formatMonthOnly(isoToMonth(cfg.base_date_start))
                            : "—"}
                        </TableCell>
                        <TableCell className="text-[#3B494A]">
                          {cfg.forecast_date_start && cfg.forecast_date_end
                            ? `${formatMonthLabel(isoToMonth(cfg.forecast_date_start))} - ${formatMonthLabel(isoToMonth(cfg.forecast_date_end))}`
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={`h-6 px-2 rounded ${
                              cfg.is_current
                                ? "bg-[#135045] text-[#88C0B2]"
                                : "bg-[#E8EFF0] text-[#3B494A]"
                            } text-xs font-semibold`}
                          >
                            {cfg.is_current ? "Ativo" : "Inativo"}
                          </Badge>
                        </TableCell>
                        <TableCell className="w-[380px]">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              className="h-8 w-[150px] justify-center rounded hover:bg-[#0000000D] text-[#00646B]"
                              onClick={() => navigate(`/simulacoes/${cfg.id}`)}
                              disabled={
                                createMutation.isPending ||
                                updateMutation.isPending ||
                                setCurrentMutation.isPending
                              }
                            >
                              <span className="text-sm">Visualizar</span>
                            </Button>
                            <Button
                              variant="ghost"
                              className="h-8 w-[110px] justify-center rounded hover:bg-[#0000000D] text-[#00646B]"
                              onClick={() => openEdit(cfg)}
                              disabled={
                                createMutation.isPending ||
                                updateMutation.isPending ||
                                setCurrentMutation.isPending
                              }
                            >
                              <Pencil className="w-4 h-4 text-[#00646B]" />
                              <span className="ml-1 text-sm">Editar</span>
                            </Button>
                            <Button
                              variant="ghost"
                              className={`h-8 w-[110px] justify-center rounded ${
                                cfg.is_current
                                  ? "hover:bg-[#FFDAD6]"
                                  : "hover:bg-[#0000000D]"
                              }`}
                              onClick={() =>
                                cfg.is_current
                                  ? requestDelete(cfg.id)
                                  : handleSetCurrent(cfg.id)
                              }
                              disabled={
                                createMutation.isPending ||
                                deleteMutation.isPending ||
                                updateMutation.isPending ||
                                setCurrentMutation.isPending
                              }
                            >
                              {cfg.is_current ? (
                                <>
                                  <Trash2 className="w-4 h-4 text-[#BA1A1A]" />
                                  <span className="ml-1 text-sm text-[#BA1A1A]">
                                    Inativar
                                  </span>
                                </>
                              ) : (
                                <>
                                  <Power className="w-4 h-4 text-[#00646B]" />
                                  <span className="ml-1 text-sm text-[#00646B]">
                                    Ativar
                                  </span>
                                </>
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {isStepsError && (
            <div className="mt-4 text-sm text-[#93000A]">
              Erro ao carregar pipeline steps. Verifique a API.
            </div>
          )}
        </div>
      </main>

      <Dialog open={dialogOpen} onOpenChange={(o) => setDialogOpen(o)}>
        <DialogContent className="max-w-[620px] p-0 overflow-hidden rounded-[28px] bg-white">
          <DialogHeader className="px-6 py-5 border-b border-[#BAC9CA] bg-[#EDF4F5]">
            <DialogTitle className="text-[22px] font-medium text-[#151C1D]">
              {mode === "create"
                ? "Criar nova configuração"
                : "Editar configuração"}
            </DialogTitle>
          </DialogHeader>

          <div className="px-6 py-5 space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-medium text-[#3B494A]">Nome</label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="h-12 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-1">
                <label className="text-xs font-medium text-[#3B494A]">
                  Mês para importação da base
                </label>
                <Select
                  value={baseMonthOnly}
                  onValueChange={(monthValue) => {
                    const year =
                      forecastStartMonth.match(/^(\d{4})-/)?.[1] ??
                      baseMonth.match(/^(\d{4})-/)?.[1] ??
                      String(new Date().getFullYear());
                    setBaseMonth(`${year}-${monthValue}`);
                  }}
                >
                  <SelectTrigger className="h-12 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]">
                    <SelectValue placeholder="Selecione o mês" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_OPTIONS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-1">
                <label className="text-xs font-medium text-[#3B494A]">
                  Mês de início da simulação
                </label>
                <Input
                  type="month"
                  value={forecastStartMonth}
                  onChange={(e) => setForecastStartMonth(e.target.value)}
                  className="h-12 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]"
                />
              </div>

              <div className="space-y-2 md:col-span-1">
                <label className="text-xs font-medium text-[#3B494A]">
                  Mês final da simulação
                </label>
                <Input
                  type="month"
                  value={forecastEndMonth}
                  onChange={(e) => setForecastEndMonth(e.target.value)}
                  className="h-12 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-[#3B494A]">
                Etapa de início da simulação
              </label>
              <Select
                value={String(startOrder)}
                onValueChange={(v) => setStartOrder(Number(v))}
              >
                <SelectTrigger className="h-12 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stepsSortedEnabled.map((s) => (
                    <SelectItem key={s.order} value={String(s.order)}>
                      {s.order} - {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-sm text-[#3B494A]">
                Será executado a partir da etapa {startOrder}.
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 h-12 rounded-xl border-[#BAC9CA] text-[#00646B]"
                onClick={closeDialog}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 h-12 rounded-xl bg-[#00646B] hover:bg-[#00646B]/90 text-white"
                onClick={handleSave}
                disabled={
                  createMutation.isPending ||
                  updateMutation.isPending ||
                  isStepsLoading
                }
              >
                {mode === "create" ? "Criar" : "Salvar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteOpen}
        onOpenChange={(o) => {
          setDeleteOpen(o);
          if (!o) setDeleteId(null);
        }}
      >
        <AlertDialogContent className="max-w-[480px] rounded-[28px] p-0 overflow-hidden bg-white border-[#BAC9CA]">
          <AlertDialogHeader className="px-6 py-5 border-b border-[#BAC9CA] bg-[#EDF4F5]">
            <AlertDialogTitle className="text-[22px] text-[#151C1D] font-medium">
              Confirmar exclusão
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#3B494A]">
              Tem certeza que deseja deletar esta configuração?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="px-6 py-5 gap-3">
            <AlertDialogCancel className="h-12 rounded-xl border-[#BAC9CA] text-[#00646B]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-12 rounded-xl bg-[#00646B] hover:bg-[#00646B]/90 text-white"
              disabled={deleteMutation.isPending || deleteId == null}
              onClick={() => void doDelete()}
            >
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
